/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApiFilter as ApiFilterContract } from './api-filter.interface';

export type ApiFilterValue =
  | Date
  | string
  | number
  | boolean
  | null
  | undefined;

type ApiFilterValueType<
  Op extends ApiFilterOperator,
  T,
  K extends keyof T,
> = NonNullable<Required<T>[K]> extends Array<infer U>
  ? U | T[K]
  : Op extends 'in'
  ? T[K][] | T[K] | undefined
  : Op extends 'pr'
  ? undefined
  : T[K] | undefined | null;

type ApiFilterExpressionBuilder<T, K extends keyof T> = {
  [O in ApiFilterOperator]: (
    value?: ApiFilterValueType<O, T, K>,
  ) => ApiFilterBuilderProxy<T>;
} & ((value?: ApiFilterValueType<'eq', T, K>) => ApiFilterBuilderProxy<T>);

type ApiFilterProperty<T> = {
  [K in keyof Required<T>]: ApiFilterExpressionBuilder<T, K> & {
    not: ApiFilterExpressionBuilder<T, K>;
  };
};

type ApiFilterWhenConditionFn<T> = (
  condition: boolean,
  whenTrue: (filter: ApiFilterBuilderProxy<T>) => void,
  whenFalse?: (filter: ApiFilterBuilderProxy<T>) => void,
) => ApiFilterBuilderProxy<T>;

type CaseWhen<T, M> = {
  match: M;
  fn: (filter: ApiFilterBuilderProxy<T>) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NoInfer<T> = [T][T extends any ? 0 : never];

type ApiFilterSwitchConditionFn<T> = <M>(
  condition: M,
  ...cases: CaseWhen<T, NoInfer<M>>[]
) => ApiFilterBuilderProxy<T>;

type ApiFilterConditionBuilder<T> = {
  if: ApiFilterWhenConditionFn<T>;
  when: ApiFilterWhenConditionFn<T>;
  switch: ApiFilterSwitchConditionFn<T>;
};

type ApiFilterBooleanFnArg<T> = (filter: ApiFilterBuilderProxy<T>) => any;

type ApiFilterBooleanFactoryFn<T> = ((
  factory: ApiFilterBooleanFnArg<T>,
) => ApiFilterBuilderProxy<T>) & {
  name: 'name' extends keyof T ? ApiFilterProperty<T>['name'] : string;
};
type ApiFilterBooleanStringWOpFn<T> = <
  O extends ApiFilterOperator,
  K extends keyof Required<T>,
>(
  key: K,
  operator: O,
  value?: ApiFilterValueType<O, T, K>,
) => ApiFilterBuilderProxy<T>;
type ApiFilterBooleanStringWoOpFn<T> = <K extends keyof Required<T>>(
  key: K,
  value: ApiFilterValueType<'eq', T, K>,
) => ApiFilterBuilderProxy<T>;
type ApiFilterBooleanFn<T> = ApiFilterBooleanFactoryFn<T> &
  ApiFilterBooleanStringWOpFn<T> &
  ApiFilterBooleanStringWoOpFn<T>;

type ApiFilterBooleanPropertyBuilder<T> = ApiFilterBooleanFn<T> &
  ApiFilterProperty<T> &
  ApiFilterNotBuilder<T> &
  ApiFilterRawBuilder<T> &
  ApiFilterConditionBuilder<T>;

type ApiFilterBooleanBuilder<T> = {
  [Bool in ApiFilterBooleanType]: ApiFilterBooleanPropertyBuilder<T>;
};

type ApiFilterWhereBuilder<T> = {
  where: ApiFilterBooleanPropertyBuilder<T>;
};

type ApiFilterNotBuilder<T> = {
  not: ApiFilterBooleanPropertyBuilder<T>;
};

type ApiFilterRawBuilder<T> = {
  raw: (raw: string) => ApiFilterBuilderProxy<T>;
};

export type ApiFilterBuilderProxy<T = any> = ApiFilterBuilder<T> &
  ApiFilterWhereBuilder<T> &
  ApiFilterBooleanBuilder<T> &
  ApiFilterNotBuilder<T> &
  ApiFilterRawBuilder<T> &
  ApiFilterProperty<T>;

export type ApiFilterOperator = (typeof API_FILTER_OPERATORS)[number];

type ApiFilterBooleanType = (typeof API_FILTER_BOOLEAN)[number];

type ApiFilterFactory<T> = (builder: ApiFilterBuilderProxy<T>) => void;

type ConditionKey = 'key' | 'operator' | 'negation' | 'boolean';

const API_FILTER_OPERATORS = [
  'eq', // equal
  'ne', // not equal
  'gt', // greater than
  'ge', // greater than or equal
  'lt', // less than
  'le', // less than or equal
  'sw', // starts with
  'ew', // ends with
  'co', // contains
  'pr', // present
  'in', // in any of the values
] as const;

const API_FILTER_BOOLEAN = ['and', 'or'] as const;

interface ApiFilterBasic<T, K extends keyof T = keyof T> {
  key: Required<K>;
  operator: ApiFilterOperator;
  value?: ApiFilterValueType<ApiFilterOperator, T, K>;
  boolean: ApiFilterBooleanType;
  inverse?: boolean;
}

interface ApiFilterNested<T> {
  factory: ApiFilterFactory<T>;
  boolean: ApiFilterBooleanType;
  inverse?: boolean;
}

interface ApiFilterRaw {
  raw: string;
  boolean: ApiFilterBooleanType;
  inverse?: boolean;
}

type ApiFilter<T> = ApiFilterBasic<T> | ApiFilterNested<T> | ApiFilterRaw;

const DEFAULT_CASE = '__DEFAULT__';

export class ApiFilterBuilder<T = Record<string, ApiFilterValue>>
  implements ApiFilterContract
{
  private filters: ApiFilter<T>[] = [];

  // Proxy-based fluent API for chained property/operator access
  public static createProxy<T>(
    builder: ApiFilterBuilder<T>,
    boolType: ApiFilterBooleanType = 'and',
  ): ApiFilterBuilderProxy<T> {
    // Helper functions for state
    const getLast = <V>(
      target: ApiFilterBuilder<T>,
      key: ConditionKey,
      defaultValue: V,
    ) => {
      return ((target as any)[`_last${key}`] ?? defaultValue) as V;
    };

    const setLast = <T>(
      target: ApiFilterBuilder<T>,
      key: ConditionKey,
      value: unknown,
    ) => {
      (target as any)[`_last${key}`] = value;
    };

    const deleteLast = (target: ApiFilterBuilder<T>, key: ConditionKey) => {
      delete (target as any)[`_last${key}`];
    };

    const reset = (target: ApiFilterBuilder<T>) => {
      deleteLast(target, 'key');
      deleteLast(target, 'operator');
      deleteLast(target, 'negation');
      deleteLast(target, 'boolean');
    };

    function handleBooleanChain(boolType: ApiFilterBooleanType) {
      const callable = function (...args: any[]) {
        if (typeof args[0] === 'function') {
          // Nested condition: .and((b) => ...)
          // Create a new builder for the nested group
          const nestedBuilder = new ApiFilterBuilder<T>();
          // Always use 'and' for nested group default
          args[0](ApiFilterBuilder.createProxy(nestedBuilder, 'and'));
          builder.addWhere(
            (b: ApiFilterBuilderProxy<T>) => {
              // Copy filters from nestedBuilder to b
              (b as any).cloneFrom(nestedBuilder);
            },
            boolType,
            getLast(builder, 'negation', false),
          );
        } else {
          const key = args[0];
          const value = args[2]
            ? args[2]
            : args[1] === 'pr'
            ? undefined
            : args[1];
          const op = args[2] ? args[1] : args[1] === 'pr' ? 'pr' : 'eq';

          builder.addWhere(
            key,
            op,
            value,
            boolType,
            getLast(builder, 'negation', false),
          );
        }

        return ApiFilterBuilder.createProxy(builder, boolType);
      };

      return new Proxy(callable, {
        get(_fn, key: string) {
          // If the key is 'not', set negation for the next property
          if (key === 'not') {
            setLast(builder, 'negation', !getLast(builder, 'negation', false));
            return new Proxy(function () {}, {
              get(_innerFn, nextKey) {
                setLast(builder, 'key', nextKey);
                // Always return a proxy for any property
                return new Proxy(function () {}, {
                  get(_innerFn2, op: string) {
                    if (
                      API_FILTER_OPERATORS.includes(op as ApiFilterOperator)
                    ) {
                      return (
                        value: ApiFilterValueType<
                          ApiFilterOperator,
                          T,
                          keyof T
                        >,
                      ) => {
                        builder.addWhere(
                          nextKey as keyof T,
                          op as ApiFilterOperator,
                          value,
                          boolType,
                          getLast(builder, 'negation', false),
                        );
                        reset(builder);

                        return ApiFilterBuilder.createProxy(builder, boolType);
                      };
                    }
                    return undefined;
                  },
                  apply(_target, _thisArg, argArray) {
                    // Support direct call: .and.not.name(1)
                    if (argArray.length > 0) {
                      builder.addWhere(
                        nextKey as keyof T,
                        'eq',
                        argArray[0],
                        boolType,
                        getLast(builder, 'negation', false),
                      );
                      reset(builder);
                    }

                    return ApiFilterBuilder.createProxy(builder, boolType);
                  },
                });
              },
            });
          }
          // Handle when/if/switch conditions by returning a function for chaining
          if (['when', 'if', 'switch'].includes(key)) {
            return (...args: any[]) => {
              // Patch: pass a proxy with the current boolType to the callback(s)
              const patchedArgs = [...args];
              if (typeof args[1] === 'function') {
                patchedArgs[1] = (_filter: ApiFilterBuilderProxy<T>) => {
                  args[1](ApiFilterBuilder.createProxy(builder, boolType));
                };
              }
              if (typeof args[2] === 'function') {
                patchedArgs[2] = (_filter: ApiFilterBuilderProxy<T>) => {
                  args[2](ApiFilterBuilder.createProxy(builder, boolType));
                };
              }
              if (key === 'switch' && Array.isArray(args.slice(1))) {
                // Patch for switch: update all caseWhen/caseDefault callbacks
                patchedArgs.length = 1; // keep condition
                for (const caseObj of args.slice(1)) {
                  patchedArgs.push({
                    match: caseObj.match,
                    fn: (_filter: ApiFilterBuilderProxy<T>) => {
                      caseObj.fn(
                        ApiFilterBuilder.createProxy(builder, boolType),
                      );
                    },
                  });
                }
              }
              (builder as any)[key](...patchedArgs);
              return ApiFilterBuilder.createProxy(builder, boolType);
            };
          }
          setLast(builder, 'boolean', boolType);
          setLast(builder, 'key', key);
          // Always return a proxy for any property
          return new Proxy(function () {}, {
            get(_innerFn, op: string) {
              // If chaining with and/or, update boolType for next property
              if (API_FILTER_BOOLEAN.includes(op as ApiFilterBooleanType)) {
                return ApiFilterBuilder.createProxy(
                  builder,
                  op as ApiFilterBooleanType,
                )[key as keyof ApiFilterBuilderProxy<T>];
              }
              if (API_FILTER_OPERATORS.includes(op as ApiFilterOperator)) {
                return (
                  value: ApiFilterValueType<ApiFilterOperator, T, keyof T>,
                ) => {
                  builder.addWhere(
                    key as keyof T,
                    op as ApiFilterOperator,
                    value,
                    boolType,
                    getLast(builder, 'negation', false),
                  );
                  reset(builder);
                  return ApiFilterBuilder.createProxy(builder, boolType);
                };
              }
              if (op === 'not') {
                setLast(
                  builder,
                  'negation',
                  !getLast(builder, 'negation', false),
                );
                return new Proxy(function () {}, {
                  get(_notFn, notOp: string) {
                    if (
                      API_FILTER_OPERATORS.includes(notOp as ApiFilterOperator)
                    ) {
                      return (
                        value: ApiFilterValueType<
                          ApiFilterOperator,
                          T,
                          keyof T
                        >,
                      ) => {
                        builder.addWhere(
                          key as keyof T,
                          notOp as ApiFilterOperator,
                          value,
                          boolType,
                          getLast(builder, 'negation', false),
                        );
                        reset(builder);
                        return ApiFilterBuilder.createProxy(builder, boolType);
                      };
                    }
                    return undefined;
                  },
                });
              }
              // Recursively return a proxy for any property
              return ApiFilterBuilder.createProxy(builder, boolType)[
                op as keyof ApiFilterBuilderProxy<T>
              ];
            },
            apply(_target, _thisArg, argArray) {
              // Support direct call: .and.name(1), or.raw('custom raw filter')
              const negation = getLast(builder, 'negation', false);
              if (argArray.length > 0) {
                if (key === 'raw') {
                  builder.addRaw(
                    argArray[0],
                    boolType,
                    getLast(builder, 'negation', false),
                  );
                } else {
                  builder.addWhere(
                    key as keyof T,
                    'eq',
                    argArray[0],
                    boolType,
                    negation,
                  );
                }
                reset(builder);
              }

              return ApiFilterBuilder.createProxy(builder, boolType);
            },
          });
        },
        apply(_target, _thisArg, argArray) {
          // Support direct call: .and((b) => ...)
          return callable(...argArray);
        },
      });
    }

    return new Proxy(builder, {
      get(target, key: string) {
        // If the property exists on the builder, return it directly (for .build(), .toString(), etc.)
        if (key in target) {
          if (typeof (target as any)[key] === 'function') {
            return (target as any)[key].bind(target);
          }

          return (target as any)[key];
        }
        // Boolean chaining: .and, .or, .and((b) => ...)
        if (API_FILTER_BOOLEAN.includes(key as ApiFilterBooleanType)) {
          return handleBooleanChain(key as ApiFilterBooleanType);
        }

        // Chaining with  .where, .wherer((b) => ...)
        if (key === 'where') {
          return handleBooleanChain('and');
        }

        // Chaining with  .not, .not((b) => ...)
        if (key === 'not') {
          setLast(
            target,
            'negation',
            !getLast<boolean>(target, 'negation', false),
          );
          return handleBooleanChain('and');
        }

        if (key === 'raw') {
          return (rawStr: string) => {
            builder.addRaw(
              rawStr,
              boolType,
              getLast(builder, 'negation', false),
            );
            return ApiFilterBuilder.createProxy(builder, boolType);
          };
        }

        // Store key for next operator/value
        setLast(target, 'key', key);
        // Always return a proxy for property access
        const callable = function (
          value: ApiFilterValueType<'eq', T, keyof T>,
        ) {
          const keyVal = getLast<string | null>(target, 'key', null);
          if (keyVal === null) throw new Error(`Key is not set for value`);
          const boolVal = getLast<ApiFilterBooleanType>(
            target,
            'boolean',
            boolType,
          );
          const negation = getLast<boolean>(target, 'negation', false);
          target.addWhere(keyVal as keyof T, 'eq', value, boolVal, negation);
          reset(target);
          return ApiFilterBuilder.createProxy(target, boolType);
        };
        return new Proxy(callable, {
          get(_fn, op: string) {
            if (API_FILTER_OPERATORS.includes(op as ApiFilterOperator)) {
              setLast(target, 'operator', op as ApiFilterOperator);
              return (
                value: ApiFilterValueType<ApiFilterOperator, T, keyof T>,
              ) => {
                const keyVal = getLast<string | null>(target, 'key', null);
                if (keyVal === null)
                  throw new Error(`Key is not set for operator`);
                const boolVal = getLast<ApiFilterBooleanType>(
                  target,
                  'boolean',
                  boolType,
                );
                const negation = getLast<boolean>(target, 'negation', false);
                const operator = op as ApiFilterOperator;
                target.addWhere(
                  keyVal as keyof T,
                  operator,
                  value,
                  boolVal,
                  negation,
                );
                reset(target);
                return ApiFilterBuilder.createProxy(target, boolType);
              };
            }
            if (op === 'not') {
              setLast(
                target,
                'negation',
                !getLast<boolean>(target, 'negation', false),
              );
              return ApiFilterBuilder.createProxy<T>(target, boolType)[
                getLast(target, 'key', '') as keyof ApiFilterBuilderProxy<T>
              ];
            }
            return undefined;
          },
        });
      },
    }) as ApiFilterBuilderProxy<T>;
  }

  public addWhere<K extends keyof Required<T>, O extends ApiFilterOperator>(
    key: K,
    operator: O,
    value: ApiFilterValueType<O, T, K>,
    bool?: ApiFilterBooleanType,
    negated?: boolean,
  ): ApiFilterBuilderProxy<T>;
  public addWhere<K extends keyof Required<T>>(
    key: K,
    value: ApiFilterValueType<'eq', T, K>,
  ): ApiFilterBuilderProxy<T>;
  public addWhere(
    factory: ApiFilterFactory<T>,
    bool?: ApiFilterBooleanType,
    negated?: boolean,
  ): ApiFilterBuilderProxy<T>;
  public addWhere<K extends keyof Required<T>, O extends ApiFilterOperator>(
    keyOrFactory: K | ApiFilterFactory<T>,
    operatorValueOrBool?:
      | O
      | ApiFilterValueType<'eq', T, K>
      | ApiFilterBooleanType,
    valueOrNegation?: ApiFilterValueType<O, T, K> | boolean,
    bool: ApiFilterBooleanType = 'and',
    negated = false,
  ): ApiFilterBuilder<T> {
    if (typeof keyOrFactory === 'function') {
      this.filters.push({
        factory: keyOrFactory,
        boolean: this.isBooleanType(operatorValueOrBool)
          ? operatorValueOrBool
          : bool,
        inverse: this.isNegation(valueOrNegation) ? valueOrNegation : negated,
      });
    } else {
      const val = (
        this.isOperator(operatorValueOrBool)
          ? valueOrNegation
          : operatorValueOrBool
      ) as ApiFilterValueType<O, T, K>;

      const operator: ApiFilterOperator = this.isOperator(operatorValueOrBool)
        ? operatorValueOrBool
        : 'eq';

      if (val === undefined && operator !== 'pr') {
        return this;
      }

      this.filters.push({
        key: keyOrFactory as keyof T,
        operator,
        value: val,
        boolean: bool,
        inverse: negated,
      });
    }

    return this;
  }

  public addRaw(
    filter: string,
    boolean: ApiFilterBooleanType = 'and',
    negated = false,
  ): ApiFilterBuilder<T> {
    this.filters.push({
      raw: filter,
      boolean,
      inverse: negated,
    });

    return this;
  }

  public if(
    condition: boolean,
    whenTrue: (filter: ApiFilterBuilderProxy<T>) => void,
    whenFalse?: (filter: ApiFilterBuilderProxy<T>) => void,
  ): ApiFilterBuilderProxy<T> {
    const proxy = ApiFilterBuilder.createProxy(this);
    if (condition) {
      whenTrue(proxy);
    } else {
      whenFalse?.(proxy);
    }
    return proxy;
  }

  public switch<M>(
    condition: M,
    ...cases: CaseWhen<T, NoInfer<M>>[]
  ): ApiFilterBuilderProxy<T> {
    const proxy = ApiFilterBuilder.createProxy(this);
    const caseCondition = cases.find(
      ({ match }) => match === condition || match === DEFAULT_CASE,
    );
    if (caseCondition) {
      caseCondition.fn(proxy);
    }
    return proxy;
  }

  public when(
    condition: boolean,
    whenTrue: (filter: ApiFilterBuilderProxy<T>) => void,
    whenFalse?: (filter: ApiFilterBuilderProxy<T>) => void,
  ): ApiFilterBuilderProxy<T> {
    const proxy = ApiFilterBuilder.createProxy(this);
    if (condition) {
      whenTrue(proxy);
    } else {
      whenFalse?.(proxy);
    }
    return proxy;
  }

  private stringifyValue(value: ApiFilterValue): string | null {
    if (value === null) {
      return 'null';
    } else if (typeof value === 'string') {
      return `"${value}"`;
    } else if (Array.isArray(value)) {
      return value.length === 0
        ? null
        : value.length === 1
        ? this.stringifyValue(value[0])
        : `"${value.join(',')}"`;
    } else if (value instanceof Date) {
      return `"${value.toISOString()}"`;
    }

    return value?.toString() ?? null;
  }

  private stringifyNestedFilter(filter: ApiFilterNested<T>): string | null {
    const builder = new ApiFilterBuilder<T>();
    filter.factory(ApiFilterBuilder.createProxy(builder, filter.boolean));
    const nestedFilters = builder.build();
    if (!nestedFilters) {
      return null;
    }

    return `(${nestedFilters})`;
  }

  private stringifyBasicFilter(filter: ApiFilterBasic<T>): string | null {
    if (filter.operator === 'in') {
      const values = Array.isArray(filter.value)
        ? filter.value
        : [filter.value];

      const conds = values
        .map((val) => {
          const key = filter.key.toString();
          const value = this.stringifyValue(val);
          if (value === null) {
            return null;
          }

          return `${key} eq ${value}`;
        })
        .filter((v) => v !== null);

      if (conds.length === 0) {
        return null;
      }

      const result = conds.join(' or ').trim();

      return conds.length > 1 ? `(${result})` : result;
    }

    const key = filter.key.toString();
    const operator = filter.operator;

    if (operator === 'pr') {
      return `${key} ${operator}`;
    }

    const value = this.stringifyValue(filter.value as ApiFilterValue);

    if (value === undefined) {
      return null;
    }

    return `${key} ${operator} ${value}`.trim();
  }

  private stringifyRawFilter(filter: ApiFilterRaw): string | null {
    return filter.raw.length > 0 ? filter.raw : null;
  }

  public build(): string | undefined {
    let result = '';

    for (const filter of this.filters) {
      const bool = filter.boolean;
      let filterStr: string | null = '';

      if (this.isRawFilter(filter)) {
        filterStr = this.stringifyRawFilter(filter);
      } else if (this.isNestedFilter(filter)) {
        filterStr = this.stringifyNestedFilter(filter);
      } else {
        filterStr = this.stringifyBasicFilter(filter);
      }

      // skip empty filters
      if (filterStr === null || filterStr.length === 0) {
        continue;
      }

      if (filter.inverse) {
        filterStr = `not ${filterStr}`;
      }

      if (result.length > 0) {
        result += ` ${bool} ${filterStr}`;
      } else {
        result += filterStr;
      }
    }

    if (result.length === 0) {
      return undefined;
    }

    return result;
  }

  public cloneFrom(other: ApiFilterBuilder<T>) {
    this.filters = [...other.filters];
  }

  public toString(): string {
    const built = this.build();
    return built === undefined ? '' : built;
  }

  private isOperator(value: unknown): value is ApiFilterOperator {
    return API_FILTER_OPERATORS.includes(value as ApiFilterOperator);
  }

  private isBooleanType(value: unknown): value is ApiFilterBooleanType {
    return API_FILTER_BOOLEAN.includes(value as ApiFilterBooleanType);
  }

  private isNegation(value: unknown): value is boolean {
    return typeof value === 'boolean';
  }

  private isNestedFilter(filter: ApiFilter<T>): filter is ApiFilterNested<T> {
    return 'factory' in filter;
  }

  private isRawFilter(filter: ApiFilter<T>): filter is ApiFilterRaw {
    return 'raw' in filter;
  }
}

export function apiFilter<
  T,
  K extends keyof Required<T>,
  O extends ApiFilterOperator,
>(
  key: K,
  operator: O,
  value?: ApiFilterValueType<O, T, K>,
): ApiFilterBuilderProxy<T>;
export function apiFilter<T>(
  factory: ApiFilterFactory<T>,
): ApiFilterBuilderProxy<T>;
export function apiFilter<T = Record<string, ApiFilterValue>>(
  raw?: ApiFilterContract,
): ApiFilterBuilderProxy<T>;
export function apiFilter<
  T = Record<string, ApiFilterValue>,
>(): ApiFilterBuilderProxy<T>;
export function apiFilter<
  T = Record<string, ApiFilterValue>,
  K extends keyof T = keyof T,
  O extends ApiFilterOperator = ApiFilterOperator,
>(
  keyOrFactoryOrRaw?: K | ApiFilterFactory<T> | ApiFilterContract,
  operatorOrValue?: O | ApiFilterValueType<'eq', T, K>,
  value?: ApiFilterValueType<O, T, K>,
): ApiFilterBuilderProxy<T> {
  const builder = new ApiFilterBuilder<T>();
  // If arguments are provided, add the initial filter
  if (keyOrFactoryOrRaw !== undefined) {
    if (keyOrFactoryOrRaw instanceof ApiFilterBuilder) {
      // If passed an instance of ApiFilterBuilder, clone its filters
      builder.cloneFrom(keyOrFactoryOrRaw);
      return ApiFilterBuilder.createProxy<T>(builder);
    }
    // raw filter or builder provided

    const isKey = typeof keyOrFactoryOrRaw === 'string';
    const isOperator = API_FILTER_OPERATORS.includes(
      keyOrFactoryOrRaw as ApiFilterOperator,
    );
    const isRaw =
      isKey &&
      !isOperator &&
      operatorOrValue === undefined &&
      value === undefined;

    if (isRaw) {
      builder.addRaw(keyOrFactoryOrRaw?.toString());
    } else {
      builder.addWhere(
        keyOrFactoryOrRaw as K,
        isOperator ? (keyOrFactoryOrRaw as O) : 'eq',
        value as ApiFilterValueType<O, T, K>,
      );
    }
  }
  // Always return the fluent proxy so chaining works without calling any method first
  return ApiFilterBuilder.createProxy<T>(builder);
}

export function caseWhen<T, M>(
  match: M,
  fn: (filter: ApiFilterBuilderProxy<T>) => void,
): CaseWhen<T, M> {
  return { match, fn };
}

export function caseDefault<T, M>(
  fn: (filter: ApiFilterBuilderProxy<T>) => void,
): CaseWhen<T, M> {
  return { match: DEFAULT_CASE as M, fn };
}
